/*
  MIT License

  Copyright (c) 2021 Cloud Four

  Permission is hereby granted, free of charge, to any person obtaining a copy
  of this software and associated documentation files (the "Software"), to deal
  in the Software without restriction, including without limitation the rights
  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  copies of the Software, and to permit persons to whom the Software is
  furnished to do so, subject to the following conditions:

  The above copyright notice and this permission notice shall be included in all
  copies or substantial portions of the Software.

  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
  SOFTWARE.
*/

const template = document.createElement("template");

// Styling range input thumbs requires you to separately define the CSS rules
// for different browsers. We store them once here for ease of maintenance.
const thumbStyles = `
  background-color: var(--image-compare-thumb-background-color);
  background-image: var(--image-compare-thumb-background-image);
  background-size: 90%;
  background-position: center center;
  background-repeat: no-repeat;
  border-radius: var(--image-compare-thumb-radius);
  border: var(--image-compare-thumb-border-size) var(--image-compare-thumb-border-color) solid;
  color: var(--image-compare-thumb-border-color);
  width: var(--image-compare-thumb-size);
  height: var(--image-compare-thumb-size);
`;

const thumbFocusStyles = `
  box-shadow: 0px 0px var(--image-compare-focus-width) var(--image-compare-focus-color);
`;

// Since the code below is a template string literal, it will not be minified or
// transpiled
template.innerHTML = /*html*/ `
  <style>
    :host {
      --image-compare-exposure: 50%;

      --image-compare-thumb-background-color: rgba(255, 255, 255, 0.45);
      --image-compare-thumb-background-image: url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2250%22%20height%3D%2224.243%22%20viewBox%3D%220%200%2050%2024.243%22%3E%3Cg%20id%3D%22Group_2%22%20data-name%3D%22Group%202%22%20transform%3D%22translate(-367%20-163.879)%22%3E%3Cg%20id%3D%22Group_1%22%20data-name%3D%22Group%201%22%20transform%3D%22translate(-36%20-13)%22%3E%3Cg%20id%3D%22download%22%20transform%3D%22translate(398%20159)%22%3E%3Cpath%20id%3D%22Path_1%22%20data-name%3D%22Path%201%22%20d%3D%22M20%2C20%2C10%2C30%2C20%2C40%22%20fill%3D%22none%22%20stroke%3D%22%23fff%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%223%22%2F%3E%3Cpath%20id%3D%22Path_2%22%20data-name%3D%22Path%202%22%20d%3D%22M40%2C20%2C50%2C30%2C40%2C40%22%20fill%3D%22none%22%20stroke%3D%22%23fff%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%223%22%2F%3E%3C%2Fg%3E%3Cpath%20id%3D%22Path_3%22%20data-name%3D%22Path%203%22%20d%3D%22M0%2C0H39%22%20transform%3D%22translate(408%20189)%22%20fill%3D%22none%22%20stroke%3D%22%23fff%22%20stroke-width%3D%223%22%2F%3E%3C%2Fg%3E%3Crect%20id%3D%22Rectangle_1%22%20data-name%3D%22Rectangle%201%22%20width%3D%2250%22%20height%3D%2220%22%20transform%3D%22translate(367%20166)%22%20fill%3D%22none%22%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E');
      --image-compare-thumb-size: clamp(3em, 10vmin, 5em);
      --image-compare-thumb-radius: 50%;
      --image-compare-thumb-border-color: transparent;
      --image-compare-thumb-border-size: 0.2rem;

      --image-compare-focus-width: 0.5rem;
      --image-compare-focus-color: rgba(0, 0, 0, 0.5);
      --image-compare-divider-width: 0.2rem;
      --image-compare-divider-color: #ffffff;

      display: flex;
      flex-direction: column;
      margin: 0;
      overflow: hidden;
      position: relative;
    }

    ::slotted(img) {
      height: auto;
      width: 100%;
    }

    ::slotted([slot='image-2']) {
      clip-path: polygon(
        calc(var(--image-compare-exposure) + var(--image-compare-divider-width)/2) 0, 
        100% 0, 
        100% 100%, 
        calc(var(--image-compare-exposure) + var(--image-compare-divider-width)/2) 100%);
    }

    slot {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
    }

    slot[name='image-2'] {
      position: absolute;
      top:0;
      filter: drop-shadow(calc(var(--image-compare-divider-width) * -1) 0 0 var(--image-compare-divider-color));
    }

    .visually-hidden {
      border: 0; 
      clip: rect(0 0 0 0); 
      clip-path: polygon(0px 0px, 0px 0px, 0px 0px);
      -webkit-clip-path: polygon(0px 0px, 0px 0px, 0px 0px);
      height: 1px; 
      margin: -1px;
      overflow: hidden;
      padding: 0;
      position: absolute;
      width: 1px;
      white-space: nowrap;
    }

    label {
      display: flex;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      margin: 0 calc(var(--image-compare-thumb-size) / -2);
      left: 0;
      right: 0;
    }

    input {
      cursor: col-resize;
      width: 100%;
      appearance: none;
      -webkit-appearance: none;
      background: none;
      border: none;
    }

    ::-moz-range-thumb {
      ${thumbStyles}
    }

    ::-webkit-slider-thumb {
      -webkit-appearance: none;
      ${thumbStyles}
    }

    input:focus::-moz-range-thumb {
      ${thumbFocusStyles}
    }

    input:focus::-webkit-slider-thumb {
      ${thumbFocusStyles}
    }
  </style>

  <slot name="image-1"></slot>
  <slot name="image-2"></slot>
  
  <label>
    <span class="visually-hidden js-label-text">
      Control how much of each overlapping image is shown. 
      0 means the first image is completely hidden and the second image is fully visible.
      100 means the first image is fully visible and the second image is completely hidden.
      50 means both images are half-shown, half-hidden.
    </span>
    <input type="range" value="50" min="0" max="100"/>
  </label>
`;

/**
 * Our ImageCompare web component class
 *
 * @attr {string} label-text - Provide additional context to screen reader users.
 *
 * @slot image-1 - Your first image. Will appear on the "left"
 * @slot image-2 - Your second image. Will appear on the "right"
 *
 * @cssprop --image-compare-thumb-background-color - The background color of the range slider handle.
 * @cssprop --image-compare-thumb-background-image - The background image of the range slider handle.
 * @cssprop --image-compare-thumb-size - The size of the range slider handle.
 * @cssprop --image-compare-thumb-radius - The border-radius of the range slider handle.
 * @cssprop --image-compare-thumb-border-color - The color of the range slider handle border.
 * @cssprop --image-compare-thumb-border-size - The width of the range slider handle border.
 *
 * @ccprop --image-compare-focus-width - The width of the range slider handle's focus outline.
 * @ccprop --image-compare-focus-color - The color of the range slider handle's focus outline.
 *
 * @ccprop --image-compare-divider-width - The width of the divider shown between the two images.
 * @ccprop --image-compare-divider-color - The color of the divider shown between the two images.
 */

class ImageCompare extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    this.shadowRoot.appendChild(template.content.cloneNode(true));

    ["input", "change"].forEach((eventName) => {
      this.shadowRoot
        .querySelector("input")
        .addEventListener(eventName, ({ target }) => {
          if (this.animationFrame) cancelAnimationFrame(this.animationFrame);

          this.animationFrame = requestAnimationFrame(() => {
            this.shadowRoot.host.style.setProperty(
              "--image-compare-exposure",
              `${target.value}%`
            );
          });
        });

      this.shadowRoot
        .querySelector("input")
        .addEventListener("mouseover", (event) => {
          const closestSplide = this.closest(".product-gallery-slider");
          if (closestSplide) {
            closestSplide.dispatchEvent(new Event("disableDrag"));
          }
        });

      this.shadowRoot
        .querySelector("input")
        .addEventListener("mouseout", (event) => {
          const closestSplide = this.closest(".product-gallery-slider");
          if (closestSplide) {
            closestSplide.dispatchEvent(new Event("enableDrag"));
          }
        });
    });

    const customLabel = this.shadowRoot.host.getAttribute("label-text");
    if (customLabel) {
      this.shadowRoot.querySelector(".js-label-text").textContent = customLabel;
    }

    const videos = this.querySelectorAll("video");
    const playableVideos = [];

    videos.forEach((video) => {
      video.addEventListener("canplay", () => {
        playableVideos.push(video);

        if (playableVideos.length === videos.length) {
          playVideos(playableVideos);
        }
      });
    });

    function playVideos(videos) {
      videos.forEach((video) => {
        video.play();
      });
    }
  }
}

customElements.define("image-compare", ImageCompare);
